import {
  isWebsiteBuilderElementFlowParentNode,
  IWebsiteBuilderNodeBase,
  IWebsiteBuilderNodeDesignStyles,
  TWebsiteBuilderElementParentNode,
  TWebsiteBuilderNode,
} from "../websiteBuilderNode";
import { isWebsiteBuilderButton, IWebsiteBuilderButton, websiteBuilderNormalizeButtonSettings } from "./websiteBuilderButton";
import { isWebsiteBuilderDivider, IWebsiteBuilderDivider, websiteBuilderNormalizeDividerSettings } from "./websiteBuilderDivider";
import { isWebsiteBuilderParagraph, IWebsiteBuilderParagraph, websiteBuilderNormalizeParagraphSettings } from "./websiteBuilderParagraph";
import { isWebsiteBuilderHeading, IWebsiteBuilderHeading, websiteBuilderNormalizeHeadingSettings } from "./websiteBuilderHeading";
import { isWebsiteBuilderLink, IWebsiteBuilderLink, websiteBuilderNormalizeLinkSettings } from "./websiteBuilderLink";
import { isWebsiteBuilderList, IWebsiteBuilderList, websiteBuilderNormalizeListSettings } from "./websiteBuilderList";
import { isWebsiteBuilderImage, IWebsiteBuilderImage, websiteBuilderNormalizeImageSettings } from "./websiteBuilderImage";
import { isWebsiteBuilderIcon, IWebsiteBuilderIcon, websiteBuilderNormalizeIconElementSettings } from "./websiteBuilderIcon";
import { isWebsiteBuilderVideo, IWebsiteBuilderVideo, websiteBuilderNormalizeVideoSettings } from "./websiteBuilderVideo";
import { isWebsiteBuilderEmbed, IWebsiteBuilderEmbed, websiteBuilderNormalizeEmbedSettings } from "./websiteBuilderEmbed";
import { isWebsiteBuilderLogo, IWebsiteBuilderLogo, websiteBuilderNormalizeLogoSettings } from "./websiteBuilderLogo";
import { isWebsiteBuilderNavigationMenu, IWebsiteBuilderNavigationMenu } from "./websiteBuilderNavigationMenu";
import { CSSProperties } from "react";
import { TWebsiteBuilderHideableNodeVisibility, websiteBuilderNormalizeHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { TWebsiteBuilderSectionEntityLayout, IWebsiteBuilderSectionEntityLayoutData, TWebsiteBuilderSectionEntityHasStretch, websiteBuilderNormalizeSectionEntityStretch, websiteBuilderNormalizeSectionEntityLayout } from "../websiteBuilderSectionEntity";
import { isWebsiteBuilderGrid } from "../container/websiteBuilderGrid";

export interface IWebsiteBuilderElementDataBase {
  type: string;
  layout: TWebsiteBuilderSectionEntityLayout;
  visibility: TWebsiteBuilderHideableNodeVisibility;
  zIndex: number;
  settings: unknown;
}

export interface IWebsiteBuilderElementBase extends IWebsiteBuilderNodeBase {
  type: "element";
  element: IWebsiteBuilderElementDataBase;
}

export type TWebsiteBuilderElement =
  | IWebsiteBuilderButton
  | IWebsiteBuilderDivider
  | IWebsiteBuilderEmbed
  | IWebsiteBuilderHeading
  | IWebsiteBuilderIcon
  | IWebsiteBuilderImage
  | IWebsiteBuilderLink
  | IWebsiteBuilderList
  | IWebsiteBuilderLogo
  | IWebsiteBuilderNavigationMenu
  | IWebsiteBuilderParagraph
  | IWebsiteBuilderVideo;

export type TWebsiteBuilderElementData = TWebsiteBuilderElement["element"];
export type TWebsiteBuilderElementType = TWebsiteBuilderElementData["type"];
export type TWebsiteBuilderElementSettings = TWebsiteBuilderElementData["settings"];

type TWebsiteBuilderElementWithBuilder = IWebsiteBuilderHeading | IWebsiteBuilderParagraph;

type TWebsiteBuilderStretchElementData = TWebsiteBuilderSectionEntityHasStretch<TWebsiteBuilderElementData>;
type TWebsiteBuilderStretchElementType = TWebsiteBuilderStretchElementData["type"];
const stretchElementTypes: TWebsiteBuilderStretchElementType[] = ["divider", "image"];

export function isWebsiteBuilderStretchElementData(
  element: TWebsiteBuilderElementData,
): element is TWebsiteBuilderStretchElementData {
  return stretchElementTypes.some(stretchElementType => stretchElementType === element.type);
}

export function isWebsiteBuilderElement(node: any): node is TWebsiteBuilderElement {
  return node?.type === "element";
}

export function isWebsiteBuilderElementWithBuilder(node: TWebsiteBuilderNode): node is TWebsiteBuilderElementWithBuilder {
  return node?.type === "element" && (
    node.element.type === "heading" ||
    node.element.type === "paragraph"
  );
}

export function websiteBuilderElementFilled(
  detached: boolean,
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderNode,
  grandParentNode: TWebsiteBuilderNode,
): boolean {
  return (
    !detached &&
    node?.element.type !== "divider" &&
    parentNode?.type === "grid-cell" &&
    isWebsiteBuilderGrid(grandParentNode) &&
    grandParentNode.container.settings.cellFill
  );
}

export function websiteBuilderElementValid(node: any): node is TWebsiteBuilderElement {
  const partialNode: Partial<TWebsiteBuilderElement> = node;
  
  const isValidLayoutData = (data: IWebsiteBuilderSectionEntityLayoutData) => {
    return (
      data?.position?.x != null &&
      data?.position?.y != null &&
      data?.dimensions?.width != null &&
      data?.dimensions?.height != null
    );
  };

  return (
    partialNode != null &&
    partialNode.id != null &&
    partialNode.type === "element" &&
    partialNode.element != null &&
    (
      partialNode.element.type === "button" ||
      partialNode.element.type === "divider" ||
      partialNode.element.type === "embed" ||
      partialNode.element.type === "heading" ||
      partialNode.element.type === "icon" ||
      partialNode.element.type === "image" ||
      partialNode.element.type === "link" ||
      partialNode.element.type === "list" ||
      partialNode.element.type === "logo" ||
      partialNode.element.type === "navigation-menu" ||
      partialNode.element.type === "paragraph" ||
      partialNode.element.type === "video"
    ) &&
    isValidLayoutData(partialNode.element.layout?.desktop) &&
    isValidLayoutData(partialNode.element.layout?.tablet) &&
    isValidLayoutData(partialNode.element.layout?.mobile) &&
    partialNode.element.zIndex != null
  );
}

export function websiteBuilderNormalizeValidElement(node: TWebsiteBuilderElement): TWebsiteBuilderElement {
  const normalizedLayout = websiteBuilderNormalizeSectionEntityLayout(node.element.layout);
  const normalizedVisibility = websiteBuilderNormalizeHideableNodeVisibility(node.element.visibility);
  
  if (isWebsiteBuilderButton(node)) {
    const normalizedSettings = websiteBuilderNormalizeButtonSettings(node.element.settings);

    const normalizedButton: IWebsiteBuilderButton = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedButton;
  } else if (isWebsiteBuilderDivider(node)) {
    const normalizedSettings = websiteBuilderNormalizeDividerSettings(node.element.settings);
    const normalizedStretch = websiteBuilderNormalizeSectionEntityStretch(node.element.stretch);

    const normalizedDivider: IWebsiteBuilderDivider = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
        stretch: normalizedStretch,
      },
    };

    return normalizedDivider;
  } else if (isWebsiteBuilderEmbed(node)) {
    const normalizedSettings = websiteBuilderNormalizeEmbedSettings(node.element.settings);

    const normalizedEmbed: IWebsiteBuilderEmbed = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedEmbed;
  } else if (isWebsiteBuilderHeading(node)) {
    const normalizedSettings = websiteBuilderNormalizeHeadingSettings(node.element.settings);

    const normalizedHeading: IWebsiteBuilderHeading = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedHeading;
  } else if (isWebsiteBuilderIcon(node)) {
    const normalizedSettings = websiteBuilderNormalizeIconElementSettings(node.element.settings);

    const normalizedIcon: IWebsiteBuilderIcon = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedIcon;
  } else if (isWebsiteBuilderImage(node)) {
    const normalizedSettings = websiteBuilderNormalizeImageSettings(node.element.settings);
    const normalizedStretch = websiteBuilderNormalizeSectionEntityStretch(node.element.stretch);

    const normalizedImage: IWebsiteBuilderImage = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
        stretch: normalizedStretch,
      },
    };

    return normalizedImage;
  } else if (isWebsiteBuilderLink(node)) {
    const normalizedSettings = websiteBuilderNormalizeLinkSettings(node.element.settings);

    const normalizedLink: IWebsiteBuilderLink = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedLink;
  } else if (isWebsiteBuilderList(node)) {
    const normalizedSettings = websiteBuilderNormalizeListSettings(node.element.settings);

    const normalizedList: IWebsiteBuilderList = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedList;
  } else if (isWebsiteBuilderLogo(node)) {
    const normalizedSettings = websiteBuilderNormalizeLogoSettings(node.element.settings);

    const normalizedLogo: IWebsiteBuilderLogo = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedLogo;
  } else if (isWebsiteBuilderNavigationMenu(node)) {
    const normalizedNavigationMenu: IWebsiteBuilderNavigationMenu = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: null,
      },
    };

    return normalizedNavigationMenu;
  } else if (isWebsiteBuilderParagraph(node)) {
    const normalizedSettings = websiteBuilderNormalizeParagraphSettings(node.element.settings);

    const normalizedParagraph: IWebsiteBuilderParagraph = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedParagraph;
  } else if (isWebsiteBuilderVideo(node)) {
    const normalizedSettings = websiteBuilderNormalizeVideoSettings(node.element.settings);

    const normalizedVideo: IWebsiteBuilderVideo = {
      id: node.id,
      type: node.type,
      element: {
        type: node.element.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.element.zIndex,
        settings: normalizedSettings,
      },
    };

    return normalizedVideo;
  } else {
    const _exhaustiveCheck: never = node;
    return _exhaustiveCheck;
  }
}

export function websiteBuilderGenerateElementDesignStyles(parentNode: TWebsiteBuilderElementParentNode): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    position: isWebsiteBuilderElementFlowParentNode(parentNode) ? "relative" : "absolute",
  };

  let styleDeclaration = "";

  styleDeclaration += `position: ${styleProperties.position};`;

  return {
    className: "website-builder-element",
    styleProperties,
    styleDeclaration,
  };
}